import React, { useMemo } from 'react';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { classes } from 'src/modules/shared/utils/styles.util';
import {
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import { GroupedMenu } from './grouped-menu';

export interface SectionSidebarMenuItem {
  id: string;
  label: string;
}

export interface SectionSidebarMenuGroup {
  id: string;
  label: string;
  items: SectionSidebarMenuItem[];
}

interface SectionSidebarMenuProps {
  items?: SectionSidebarMenuItem[];
  groups?: SectionSidebarMenuGroup[];
  activeId?: string;
  onSelect: (id: string) => void;
  className?: string;
  contentClassName?: string;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
}

/**
 * Renders menu items as Tab components
 */
const MenuItems: React.FC<{ items: SectionSidebarMenuItem[] }> = ({
  items,
}) => (
  <>
    {items.map((item) => (
      <Tab key={item.id} value={item.id} label={item.label} />
    ))}
  </>
);

export const SectionSidebarMenu: React.FC<SectionSidebarMenuProps> = ({
  items,
  groups,
  activeId,
  onSelect,
  className,
  contentClassName,
  variant = 'secondary',
  appearance = 'filled',
}) => {
  const { hasGroups, flatItems, resolvedActiveId } = useMemo(() => {
    const hasGroupsData = Array.isArray(groups) && groups.length > 0;
    const flatItemsList = hasGroupsData
      ? (groups || []).flatMap((group) => group.items)
      : items || [];
    const resolvedActiveIdValue = activeId || flatItemsList[0]?.id || '';

    return {
      hasGroups: hasGroupsData,
      flatItems: flatItemsList,
      resolvedActiveId: resolvedActiveIdValue,
    };
  }, [groups, items, activeId]);

  const contentClasses = classes('mt-2', contentClassName);

  return (
    <Panel
      variant="neutral"
      appearance="ghost"
      className={classes('sticky h-fit', className)}
    >
      <div className={contentClasses}>
        {hasGroups ? (
          <div className="flex flex-col gap-8">
            {(groups || []).map((group) => (
              <GroupedMenu
                key={group.id}
                group={group}
                activeId={resolvedActiveId}
                onSelect={onSelect}
                variant={variant}
                appearance={appearance}
              />
            ))}
          </div>
        ) : (
          <Tabs
            value={resolvedActiveId}
            onChange={onSelect}
            orientation="vertical"
            variant={variant}
            appearance={appearance}
            size="md"
            className="w-full"
            align="left"
          >
            <MenuItems items={flatItems} />
          </Tabs>
        )}
      </div>
    </Panel>
  );
};
